import posthog from "posthog-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { POSTHOG_EVENTS } from "../constants/PostHogEvents";
import postHogService from "../services/PostHogService";
import { useAppStore } from "./useStores";

interface QueuedEvent {
	eventName: POSTHOG_EVENTS | string;
	properties?: Record<string, any>;
}

export const usePostHog = () => {
	const { authStore } = useAppStore();
	const [isPostHogLoaded, setIsPostHogLoaded] = useState(false);
	const eventQueueRef = useRef<QueuedEvent[]>([]);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	// Monitor PostHog loading state using public API
	useEffect(() => {
		const checkPostHogLoaded = () => {
			// Use public API method instead of private __loaded property
			try {
				// PostHog is considered loaded if we can access its methods
				if (typeof posthog.capture === "function" && !isPostHogLoaded) {
					setIsPostHogLoaded(true);

					// Process queued events
					if (eventQueueRef.current.length > 0) {
						console.log("PostHog loaded, processing queued events:", eventQueueRef.current.length);
						eventQueueRef.current.forEach(({ eventName, properties }) => {
							postHogService.trackEvent(eventName, properties);
						});
						eventQueueRef.current = []; // Clear the queue
					}
				}
			} catch (error) {
				// PostHog not ready yet, continue polling
			}
		};

		// Check immediately
		checkPostHogLoaded();

		// Set up polling only if not already loaded
		if (!isPostHogLoaded && !intervalRef.current) {
			intervalRef.current = setInterval(checkPostHogLoaded, 100);
		}

		// Cleanup function
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [isPostHogLoaded, authStore.user]); // Removed eventQueue from dependencies

	const trackEvent = useCallback(
		(eventName: POSTHOG_EVENTS | string, properties?: Record<string, any>) => {
			if (isPostHogLoaded) {
				// PostHog is loaded, send event immediately
				postHogService.trackEvent(eventName, properties);
			} else {
				// PostHog not loaded yet, queue the event
				eventQueueRef.current = [...eventQueueRef.current, { eventName, properties }];
			}
		},
		[authStore.user, isPostHogLoaded],
	);

	return {
		trackEvent,
	};
};

export default usePostHog;
